import { Active, DragEndEvent, DragStartEvent, Over, UniqueIdentifier } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import {
  AssignOrderToRailcarTrackPositionCommand,
  AssignRailcarTrackPositionCommand,
  CarrierVisitDirection,
  DeleteRailcarTrackPositionCommand,
  IssueType,
  OrderStatus,
  UpdateRailcarSequenceCommand,
} from '@planning/app/api'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { IRailcarTrackPositionItem } from '@planning/rt-stores/railTrack/RailcarTrackPositionItem'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { orderService, railVisitService } from '@planning/services'
import _ from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import { RailcarPlanningContainerProps } from '../Components/Molecules/RailcarPlanningContainer'
import { RailcarPlanningRailcarProps } from '../Components/Molecules/RailcarPlanningRailcar'
import { RailcarPlanningRailtrackProps } from '../Components/Molecules/RailcarPlanningRailtrack'

export enum RailcarPlanningDraggableEnum {
  UNASSIGNED = 'UNASSIGNED',
  RAILCAR = 'RAILCAR',
  CONTAINER = 'CONTAINER',
}

export class RailcarPlanningViewStore {
  isLoading = false
  error?: string | null
  railTrackFilter?: string | null
  containerFilter?: string | null
  draggedEntityId?: UniqueIdentifier

  currentZoomLevel = 1

  constructor(
    public direction: CarrierVisitDirection,
    public railVisitItem: IRailVisitItem,
  ) {
    makeObservable(this, {
      isLoading: observable,
      error: observable,
      currentZoomLevel: observable,

      railTrackFilter: observable,
      containerFilter: observable,
      draggedEntityId: observable,

      unassignedContainers: computed,
      filteredUnassignedContainers: computed,
      railtracks: computed,
      ordersById: computed,

      draggedContainer: computed,

      onEntityDragStart: action,
      onEntityDragEnd: action,

      onContainerDragEnd: action,

      assignContainerToRailcar: action,
      unassignContainer: action,

      setLoading: action,
      setError: action,
      setCurrentZoomLevel: action,

      setDraggedEntityId: action,
      setRailTrackFilter: action,
      setContainerFilter: action,
    })
  }

  onEntityDragStart(event: DragStartEvent) {
    this.setDraggedEntityId(event.active.id)
  }

  async onEntityDragEnd(event: DragEndEvent) {
    const { active, over } = event

    this.setDraggedEntityId()
    if (!over) return

    const activeId = active.id.toString()
    const overId = over.id.toString()

    const isDragginContainer = isRailcarPlanningIdentifierOfType(
      activeId,
      RailcarPlanningDraggableEnum.CONTAINER,
    )
    const isDragginRailcar = isRailcarPlanningIdentifierOfType(
      activeId,
      RailcarPlanningDraggableEnum.RAILCAR,
    )

    if (isDragginContainer) await this.onContainerDragEnd(activeId, overId)
    if (isDragginRailcar) await this.onRailcarDragEnd(activeId, overId, active, over)
  }

  async onContainerDragEnd(activeId: string, overId: string) {
    const orderId = extractRailcarPlanningNumberIdentifierByType(
      activeId,
      RailcarPlanningDraggableEnum.CONTAINER,
    )

    const isDroppedInUnassignedArea = isRailcarPlanningIdentifierOfType(
      overId,
      RailcarPlanningDraggableEnum.UNASSIGNED,
    )

    const isDroppedInContainerInUnassignedArea =
      isRailcarPlanningIdentifierOfType(overId, RailcarPlanningDraggableEnum.CONTAINER) &&
      this.unassignedContainers?.some(
        container =>
          container.id ===
          extractRailcarPlanningNumberIdentifierByType(
            overId,
            RailcarPlanningDraggableEnum.CONTAINER,
          ),
      )

    if (isDroppedInUnassignedArea || isDroppedInContainerInUnassignedArea) {
      this.unassignContainer(orderId)
    } else {
      this.assignContainerToRailcar(orderId, overId)
    }
  }

  async onRailcarDragEnd(
    activeId: string,
    targetId: string,
    activeEntity: Active,
    targetEntity: Over,
  ) {
    const activeRailcarId = extractRailcarPlanningIdentifierByType(
      activeId,
      RailcarPlanningDraggableEnum.RAILCAR,
    )

    const activeRailcar = this.railtracks
      .flatMap(railtrack => railtrack.railcars || [])
      .find(rc => rc.id === activeRailcarId)

    if (activeRailcar?.isGap) return

    const targetRailcarId = extractRailcarPlanningIdentifierByType(
      targetId,
      RailcarPlanningDraggableEnum.RAILCAR,
    )

    if (activeRailcarId === targetRailcarId) return

    const targetRailtrack = this.railtracks?.find(railtrack =>
      railtrack.railcars?.some(
        rc => rc.id === Number(targetRailcarId) || rc.id === targetRailcarId,
      ),
    )

    if (!targetRailtrack) return

    const oldIndex = targetRailtrack.railcars.findIndex(rc => rc.id === Number(activeRailcarId))
    const newIndex = targetRailtrack.railcars.findIndex(rc => rc.id === Number(targetRailcarId))

    targetRailtrack.railcars = arrayMove(targetRailtrack.railcars, oldIndex, newIndex)

    const activeRailcarData = activeEntity.data?.current as RailcarPlanningRailcarProps
    const targetRailcarData = targetEntity.data?.current as RailcarPlanningRailcarProps

    const railVisitId = this.railVisitItem.id
    const railTrackId = targetRailcarData.railTrackId
    const railcarTrackPositionId = activeRailcarData.railcarTrackPositionId

    const railcarSequenceNumber = targetRailcarData.sequence || 0
    await this.updateSequence(
      railVisitId,
      railTrackId,
      railcarTrackPositionId,
      railcarSequenceNumber,
    )
  }

  async assignToRailcarTrackPosition(orderId: number, railcarTrackPositionId?: number | null) {
    const cmd: AssignOrderToRailcarTrackPositionCommand = {
      orderId,
      railcarTrackPositionId,
    }

    try {
      this.setLoading(true)
      this.setError()

      await orderService.assignToRailcarTrackPosition(cmd)
    } catch (error) {
      this.setError('Failed to assign container to railcar')
    } finally {
      this.setLoading(false)
    }
  }

  async updateRailtrack(railVisitId: number, newTrackId: string, railcarTrackPositionId: number) {
    const cmd: AssignRailcarTrackPositionCommand = {
      ids: [railcarTrackPositionId],
      railVisitId,
      newTrackId,
    }

    try {
      this.setLoading(true)
      this.setError()

      await railVisitService.assignRailcarTrackPosition(cmd)
    } catch (error) {
      this.setError('Failed to update railcar sequence')
    } finally {
      this.setLoading(false)
    }
  }

  async updateSequence(
    railVisitId: number,
    railTrackId: string,
    railcarTrackPositionId: number,
    railcarSequenceNumber: number,
  ) {
    const cmd: UpdateRailcarSequenceCommand = {
      direction: this.direction,
      railVisitId,
      railTrackId,
      railcarTrackPositionIds: [railcarTrackPositionId],
      railcarSequenceNumber,
    }

    try {
      this.setLoading(true)
      this.setError()

      await railVisitService.updateRailcarSequence(cmd)
    } catch (error) {
      this.setError('Failed to update railcar sequence')
    } finally {
      this.setLoading(false)
    }
  }

  async deleteRailcar(railcarTrackPositionId: number, railVisitId: number) {
    try {
      this.setLoading(true)
      this.setError()

      const cmd: DeleteRailcarTrackPositionCommand = {
        ids: [railcarTrackPositionId],
        railVisitId,
      }

      await railVisitService.deleteRailCar(cmd)
    } catch (error) {
      this.setError('Failed to delete railcar')
    } finally {
      this.setLoading(false)
    }
  }

  get railtracks(): RailcarPlanningRailtrackProps[] {
    const railtracks = this.railVisitItem.railTracks.map<RailcarPlanningRailtrackProps>(
      railTrack => ({
        id: railTrack.id,
        trackName: railTrack.name,
        railcars: this.railVisitItem.railcarTrackPositions
          .filter(
            position =>
              position.data.railTrackId === railTrack.id &&
              position.data.direction === this.direction,
          )
          .sort((a, b) => a.data.railcarSequenceNumber - b.data.railcarSequenceNumber)
          .map<RailcarPlanningRailcarProps>(position => this.mapRailcarToRailcarProps(position)),
      }),
    )

    railtracks.forEach(railtrack => {
      this.fillRailtrackGaps(railtrack)
    })

    return railtracks
  }

  private fillRailtrackGaps(railtrack: RailcarPlanningRailtrackProps) {
    const filledRailcars: RailcarPlanningRailcarProps[] = []
    const finalSequence = Math.max(...railtrack.railcars.map(rc => rc.sequence || 0))

    for (let i = 1; i <= finalSequence; i++) {
      const currentRailcar = railtrack.railcars.find(railcar => railcar.sequence === i)

      if (currentRailcar) {
        filledRailcars.push(currentRailcar)
      } else {
        const placeholderRailcar: RailcarPlanningRailcarProps = {
          id: `${railtrack.id}-gap-${i}`,
          railTrackId: railtrack.id,
          railcarTrackPositionId: -1,
          number: 'GAP',
          sequence: i,
          isGap: true,
        }

        filledRailcars.push(placeholderRailcar)
      }
    }

    railtrack.railcars = filledRailcars
  }

  get unassignedContainers(): RailcarPlanningContainerProps[] {
    return this.railVisitItem.orders
      .filter(order => !order.data.railcarId && order.data.direction === this.direction)
      .map<RailcarPlanningContainerProps>(order => this.mapOrderToContainerProps(order))
  }

  get ordersById() {
    return _(this.railVisitItem.orders)
      .groupBy(order => order.id)
      .mapValues(orders => orders[0])
      .value()
  }

  get filteredUnassignedContainers(): RailcarPlanningContainerProps[] {
    if (!this.containerFilter) return this.unassignedContainers

    const filter = this.containerFilter.toLowerCase()

    return this.unassignedContainers.filter(container =>
      container.number.toLowerCase().includes(filter),
    )
  }

  // The currently dragged container based on draggedEntityId
  get draggedContainer() {
    if (!this.draggedEntityId) return
    const containerId = extractRailcarPlanningNumberIdentifierByType(
      this.draggedEntityId,
      RailcarPlanningDraggableEnum.CONTAINER,
    )

    return (
      this.unassignedContainers?.find(container => container.id === containerId) ||
      this.railtracks
        ?.flatMap(railtrack => railtrack.railcars || [])
        .flatMap(railcar => railcar.containers || [])
        .find(container => container.id === containerId)
    )
  }

  async assignContainerToRailcar(orderId: number, targetId: UniqueIdentifier) {
    let targetRailcarId

    const isDroppedInRailcarArea = isRailcarPlanningIdentifierOfType(
      targetId,
      RailcarPlanningDraggableEnum.RAILCAR,
    )

    const isDroppedInContainerArea = isRailcarPlanningIdentifierOfType(
      targetId,
      RailcarPlanningDraggableEnum.CONTAINER,
    )

    // The user dropped the container on a railcar
    if (isDroppedInRailcarArea) {
      targetRailcarId = extractRailcarPlanningNumberIdentifierByType(
        targetId,
        RailcarPlanningDraggableEnum.RAILCAR,
      )
    }

    // The user dropped the container on a container inside a railcar
    if (isDroppedInContainerArea) {
      const targetContainerId = extractRailcarPlanningNumberIdentifierByType(
        targetId,
        RailcarPlanningDraggableEnum.CONTAINER,
      )
      targetRailcarId = findRailCarByContainerId(this.railtracks || [], targetContainerId)
    }

    if (targetRailcarId) {
      const activeRailcar = this.railtracks
        ?.flatMap(railtrack => railtrack.railcars || [])
        .find(rc => rc.containers?.some(c => c.id === orderId))

      const targetRailcar = this.railtracks
        ?.flatMap(railtrack => railtrack.railcars || [])
        .find(rc => rc.id === targetRailcarId)

      if (activeRailcar?.id === targetRailcar?.id) return

      if (targetRailcar)
        await this.assignToRailcarTrackPosition(orderId, targetRailcar.railcarTrackPositionId)
    }
  }

  async unassignContainer(orderId: number) {
    const isContainerInUnassignedArea = this.unassignedContainers.some(c => c.id === orderId)
    if (isContainerInUnassignedArea) return

    await this.assignToRailcarTrackPosition(orderId, null)
  }

  setLoading(loading: boolean) {
    this.isLoading = loading
  }

  setError(error?: string) {
    this.error = error
  }

  setDraggedEntityId(id?: UniqueIdentifier) {
    this.draggedEntityId = id
  }

  setCurrentZoomLevel(zoomLevel: number) {
    this.currentZoomLevel = zoomLevel
  }

  setRailTrackFilter(filter: string) {
    this.railTrackFilter = filter
  }

  setContainerFilter(filter: string) {
    this.containerFilter = filter
  }

  private mapRailcarToRailcarProps(
    position: IRailcarTrackPositionItem,
  ): RailcarPlanningRailcarProps {
    const containers = this.railVisitItem.orders
      .filter(
        order =>
          order.data.railcarId === position.data.railcarId &&
          order.data.direction === this.direction,
      )
      .map<RailcarPlanningContainerProps>(order => this.mapOrderToContainerProps(order))

    const podSet = new Set(containers.map(c => c.pod).filter(Boolean))
    const hasOrdersWithDifferentPodsAssigned = podSet.size > 1
    const hasExceedingTEU =
      containers.reduce((sum, c) => sum + (c.size ?? 20) / 20, 0) >
      (position.data.railcarMaxTEU || Infinity)

    return {
      id: position.data.railcarId,
      railTrackId: position.data.railTrackId,
      number: position.data.railcarName,
      railcarTrackPositionId: position.data.id,
      sequence: position.data.railcarSequenceNumber,
      railcarMaxTEU: position.data.railcarMaxTEU,
      isMissingRailcar: position.data.isMissingRailcar,
      hasOrdersWithDifferentPodsAssigned,
      hasExceedingTEU,
      isPrepped:
        (this.direction === CarrierVisitDirection.Outbound &&
          !!position.data.loadPreparationCheckedDate) ||
        (this.direction === CarrierVisitDirection.Inbound && !!position.data.checkinDate),
      isChecked: this.direction === CarrierVisitDirection.Outbound && !!position.data.checkoutDate,
      containers,
    }
  }

  private mapOrderToContainerProps(order: IOrderItem): RailcarPlanningContainerProps {
    const d = order.data
    const journey = order.containerJourney

    return {
      id: order.id,
      number: d.containerNumber ?? '',
      isoCode: d.containerIsoCode ?? '',
      size: d.containerLength ?? 20,
      weight: d.grossWeight ?? 0,
      status: journey?.status ?? 'Expected',

      pod: d.portOfDischarge,
      pol: d.portOfLoading,
      direction: d.direction,
      shippingLine: d.shippingLine,
      consignee: d.consignee,
      referenceNumber: d.shippingOrderReference,

      isEmpty: d.isEmpty,
      isOog: d.isOOG,
      isDangerous: d.isDangerous || (d.imoClasses?.length ?? 0) > 0,

      isReefer: !!journey?.data.coolingOrderId,

      isDamaged: d.isDamaged,

      hasHolds: order.hasActiveHold,

      hasIssue: order.notResolvedIssues?.some(
        issue => issue.data.issueType === IssueType.Shortlanded,
      ),

      orderItem: order, // for holds
    }
  }

  getSelectedContainer = (id?: UniqueIdentifier) => {
    if (!id || !isRailcarPlanningIdentifierOfType(id, RailcarPlanningDraggableEnum.CONTAINER))
      return

    return this.ordersById[
      extractRailcarPlanningNumberIdentifierByType(id, RailcarPlanningDraggableEnum.CONTAINER)
    ]
  }

  isSelectedContainerDeparted = (id?: UniqueIdentifier) =>
    this.getSelectedContainer(id)?.data.status === OrderStatus.Fulfilled

  getTargetRailcarOfContainerDroppedEvent(
    event: DragEndEvent,
  ): RailcarPlanningRailcarProps | undefined {
    const { active, over } = event

    if (!over) return

    const isDragginContainer = isRailcarPlanningIdentifierOfType(
      active.id,
      RailcarPlanningDraggableEnum.CONTAINER,
    )

    if (!isDragginContainer) return

    const isOverRailcar = isRailcarPlanningIdentifierOfType(
      over.id,
      RailcarPlanningDraggableEnum.RAILCAR,
    )

    if (isOverRailcar) {
      const targetRailcar = this.railtracks
        .flatMap(railtrack => railtrack.railcars || [])
        .find(
          railcar =>
            railcar.id ===
            extractRailcarPlanningNumberIdentifierByType(
              over.id,
              RailcarPlanningDraggableEnum.RAILCAR,
            ),
        )

      return targetRailcar
    }

    const isOverContainer = isRailcarPlanningIdentifierOfType(
      over.id,
      RailcarPlanningDraggableEnum.CONTAINER,
    )

    if (isOverContainer) {
      const targetId = extractRailcarPlanningNumberIdentifierByType(
        over.id,
        RailcarPlanningDraggableEnum.CONTAINER,
      )

      const isOverContainerInRailcar = findRailCarByContainerId(this.railtracks || [], targetId)

      if (isOverContainerInRailcar)
        return this.railtracks
          .flatMap(railtrack => railtrack.railcars || [])
          .find(railcar => railcar.id === isOverContainerInRailcar)
    }
  }
}

export const isRailcarPlanningIdentifierOfType = (
  identifier: UniqueIdentifier,
  key: RailcarPlanningDraggableEnum,
) => identifier.toString().startsWith(`${key}:`)

export const extractRailcarPlanningNumberIdentifierByType = (
  identifier: UniqueIdentifier,
  key: RailcarPlanningDraggableEnum,
) => Number(extractRailcarPlanningIdentifierByType(identifier, key))

export const extractRailcarPlanningIdentifierByType = (
  identifier: UniqueIdentifier,
  key: RailcarPlanningDraggableEnum,
) => identifier.toString().replace(`${key}:`, '')

export const findRailCarByContainerId = (
  railtracks: RailcarPlanningRailtrackProps[],
  containerId: number,
) => {
  let foundId: UniqueIdentifier | null = null

  railtracks.forEach(railTrack => {
    railTrack.railcars.forEach(railcar => {
      if (railcar.containers?.some(c => c.id === containerId)) {
        foundId = railcar.id
      }
    })
  })

  return foundId
}
